
import React, { useEffect, useState } from 'react';
import { usePlayerSimilarity } from '../lib/player-similarity.context';
import { SimilarityCriteriaConfig } from './SimilarityCriteriaConfig';
import { SimilarPlayersResults } from './SimilarPlayersResults';

interface EnhancedPlayerSimilaritySectionProps {
  playerId: string;
  playerName: string;
}

export const EnhancedPlayerSimilaritySection: React.FC<EnhancedPlayerSimilaritySectionProps> = ({
  playerId,
  playerName
}) => {
  const {
    selectedPlayer,
    isLoading,
    error,
    handlePlayerSelection,
    similarPlayers,
    clearSearch
  } = usePlayerSimilarity();

  // Track if we're in the process of updating to prevent infinite loops
  const [isUpdating, setIsUpdating] = useState(false);

  // When the component mounts or the playerId changes, update the player similarity context
  useEffect(() => {
    // Skip if we're already updating or if we don't have valid player data
    if (isUpdating || !playerId || !playerName) {
      return;
    }

    // Only update if the player has changed or no player is selected
    if (!selectedPlayer || selectedPlayer.id !== playerId) {
      // Set updating flag to prevent infinite loops
      setIsUpdating(true);
      
      // Update with the new player - this will clear any previous results
      handlePlayerSelection(playerId, playerName)
        .finally(() => {
          // Reset updating flag when done
          setIsUpdating(false);
        });
    }
  }, [playerId, playerName, selectedPlayer, handlePlayerSelection, isUpdating]);

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-[#CECFD2]">Similar Players</h2>
      </div>
      
      <div className="mb-6 bg-[#121212] rounded-lg shadow-lg border border-gray-500 p-6">
      
      {/* Error message */}
      {error && similarPlayers.length === 0 && (
        <div className="mt-4 p-4 rounded bg-red-900/20 text-red-200">
          <p>{error}</p>
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && similarPlayers.length === 0 && (
        <div className="mt-4 p-4 rounded bg-blue-900/20 text-blue-200">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
            <span>Loading player details...</span>
          </div>
        </div>
      )}

      {/* Similarity Criteria Configuration */}
      {selectedPlayer && <SimilarityCriteriaConfig />}
      
      {/* Similar Players Results */}
      <SimilarPlayersResults />
      </div>
    </div>
  );
};
